import React, { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowPathIcon, CalendarDaysIcon, HomeModernIcon, ListBulletIcon } from '@heroicons/react/16/solid';
import AppSidebar from './AppSidebar';
import { Navbar } from './common/Catalyst/navbar';
import { SidebarLayout } from './common/Catalyst/sidebar-layout';
import LoadingSpinner from './LoadingSpinner';
import { amenityService, reservationService } from './clients/ApiClients';
import { AmenityReturn, ReservationReturn } from './clients/rust-api-client';
import {
  canManageAmenities,
  canManageReservations,
} from './constants/UserTypes';
import { getUserTypeFromToken } from './utils/TokenUtils';
import { FilterBar } from './violation/linear/FilterBar';
import { GroupedList } from './violation/linear/GroupedList';
import { PropertyPicker } from './violation/linear/PropertyPicker';
import { CreateButton } from './shell/CreateButton';
import { useCommandPalette } from './shell/CommandPaletteProvider';
import { useHoaDirectory } from './hoa/useHoaDirectory';
import { ReservationRow } from './reservation/ReservationRow';
import { ReservationStatusIcon } from './reservation/ReservationStatusIcon';
import { ReservationCalendar } from './reservation/ReservationCalendar';
import { pickCalendarHoa } from './reservation/calendarHoa';
import { useSyncedAmenities, useSyncedReservations } from './sync/reservationHooks';
import {
  DEFAULT_FILTERS,
  ReservationFilters,
  ReservationStatus,
  STATUSES,
  applyFilters,
  groupByStatus,
  reservationErrorMessage,
  statusLabel,
} from './reservation/reservationMeta';
import { ErrorAlert } from './common/Custom/ErrorAlert';

/**
 * Reservations (#642): the approval queue and the calendar.
 *
 * The list opens on **Pending** — the requests waiting on a manager — and
 * clearing the status filter shows everything. The calendar is one
 * association at a time (its amenities are its columns), so on the
 * all-associations page it asks which.
 *
 * Mounted at `/reservations`, `/hoa/:hoaId/reservations` and
 * `/hoa/:hoaId/home/:homeId/reservations`.
 */
type View = 'list' | 'calendar';
const VIEW_KEY = 'reservationViewMode';
const CALENDAR_HOA_KEY = 'reservationCalendarHoa';
const PAGE_SIZE = 200;

const ReservationsPage: React.FC = () => {
  const { hoaId, homeId } = useParams<{ hoaId?: string; homeId?: string }>();
  const navigate = useNavigate();
  const userType = getUserTypeFromToken();
  const canBook = canManageReservations(userType);
  const canSetUp = canManageAmenities(userType);
  const { openNewReservation, openNewReservationSeries } = useCommandPalette();

  const [view, setView] = useState<View>(() => {
    try {
      return localStorage.getItem(VIEW_KEY) === 'calendar' ? 'calendar' : 'list';
    } catch {
      return 'list';
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(VIEW_KEY, view);
    } catch {
      // Storage unavailable: the choice lasts for this page only.
    }
  }, [view]);
  // A home's own bookings are a list; the calendar is per association.
  const effectiveView: View = homeId ? 'list' : view;

  const [filters, setFilters] = useState<ReservationFilters>(DEFAULT_FILTERS);
  const [hoaFilter, setHoaFilter] = useState<string[]>([]);
  const [fetchedRows, setRows] = useState<ReservationReturn[]>([]);
  const [fetchedTotal, setTotal] = useState(0);
  const [fetching, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const spansHoas = !hoaId;
  const directory = useHoaDirectory({ enabled: spansHoas, withHomes: false });
  const [calendarHoaId, setCalendarHoaIdRaw] = useState<string | null>(hoaId ?? null);
  // The all-associations calendar remembers its association, and the
  // first time picks one that has bookable amenities rather than the
  // alphabetically first (which may have none, or be inactive).
  const setCalendarHoaId = useCallback((id: string) => {
    setCalendarHoaIdRaw(id);
    try {
      localStorage.setItem(CALENDAR_HOA_KEY, id);
    } catch {
      // Storage unavailable: the choice lasts for this page only.
    }
  }, []);
  // Every association's active amenities, for choosing where the calendar
  // opens; null without the sync engine's copy.
  const allSyncedAmenities = useSyncedAmenities({});
  useEffect(() => {
    if (hoaId) {
      setCalendarHoaIdRaw(hoaId);
      return;
    }
    if (calendarHoaId || !directory.hoas.length || effectiveView !== 'calendar') return;
    let live = true;
    let remembered: string | null = null;
    try {
      remembered = localStorage.getItem(CALENDAR_HOA_KEY);
    } catch {
      remembered = null;
    }
    // With the copy, "has a bookable amenity" is answered from memory
    // rather than one request per association.
    const bookable = allSyncedAmenities
      ? async (id: string) =>
          allSyncedAmenities.some((a) => a.hoa_id === id && a.booking_mode !== 'info_only')
      : undefined;
    pickCalendarHoa(directory.hoas, remembered, bookable).then(
      (id) => live && id && setCalendarHoaIdRaw(id)
    );
    return () => {
      live = false;
    };
  }, [hoaId, calendarHoaId, directory.hoas, effectiveView, allSyncedAmenities]);

  const [fetchedAmenities, setAmenities] = useState<AmenityReturn[]>([]);
  const amenityHoa = hoaId ?? (effectiveView === 'calendar' ? calendarHoaId : null);
  // From the sync engine's copy when there is one (#561).
  const syncedAmenities = useSyncedAmenities({ hoaId: amenityHoa });
  const amenities = amenityHoa ? syncedAmenities ?? fetchedAmenities : fetchedAmenities;
  const haveSyncedAmenities = syncedAmenities !== null;
  useEffect(() => {
    setAmenities([]);
    const forHoa = amenityHoa;
    if (!forHoa || haveSyncedAmenities) return;
    let live = true;
    amenityService
      .listAmenities({ hoaId: forHoa })
      .then((r) => live && setAmenities(r.data.amenities))
      .catch(() => {
        // The list still works without amenity names for its filter.
      });
    return () => {
      live = false;
    };
  }, [amenityHoa, haveSyncedAmenities]);

  // The status filter goes to the server, so the default — pending — is a
  // small request however long the association's history.
  const statusParam = filters.statuses.join(',');
  const historyOnly =
    filters.statuses.length > 0 && filters.statuses.every((s) => s !== 'pending' && s !== 'approved');
  // From the sync engine's copy when there is one (#561): the same query,
  // answered from memory and kept current — no request, no page limit.
  const synced = useSyncedReservations({
    hoaId,
    homeId,
    statuses: filters.statuses,
    newestFirst: historyOnly || filters.statuses.length === 0,
  });
  const haveSynced = synced !== null;
  const rows = synced ?? fetchedRows;
  const total = synced ? synced.length : fetchedTotal;
  const loading = !haveSynced && fetching;
  useEffect(() => {
    if (effectiveView !== 'list' || haveSynced) return;
    let live = true;
    setLoading(true);
    setError(null);
    reservationService
      .listReservations({
        hoaId,
        homeId,
        status: statusParam || undefined,
        newestFirst: historyOnly || filters.statuses.length === 0,
        perPage: PAGE_SIZE,
      })
      .then((r) => {
        if (!live) return;
        setRows(r.data.reservations);
        setTotal(r.data.total);
      })
      .catch((err) => live && setError(reservationErrorMessage(err, "Couldn't load reservations.")))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
    // `filters.statuses` is read through its serialised form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoaId, homeId, statusParam, effectiveView, refreshKey, haveSynced]);

  const visible = useMemo(() => {
    const filtered = applyFilters(rows, filters);
    return hoaFilter.length ? filtered.filter((r) => hoaFilter.includes(r.hoa_id)) : filtered;
  }, [rows, filters, hoaFilter]);
  const groups = useMemo(() => groupByStatus(visible), [visible]);

  const amenityOptions = useMemo(() => {
    const names = new Map<string, string>();
    amenities.forEach((a) => names.set(a.id, a.name));
    rows.forEach((r) => names.set(r.amenity_id, r.amenity_name));
    return Array.from(names, ([value, label]) => ({ value, label })).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [amenities, rows]);
  const hoaOptions = useMemo(
    () =>
      Array.from(new Set(rows.map((r) => r.hoa_id)))
        .map((id) => ({ value: id, label: directory.hoaName(id) ?? id }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [rows, directory]
  );

  const open = useCallback(
    (r: ReservationReturn) => navigate(`/hoa/${r.hoa_id}/reservations/${r.id}`),
    [navigate]
  );

  const create = () =>
    openNewReservation({
      ...(hoaId || homeId ? { scope: { hoaId, homeId }, lockScope: true } : {}),
      onCreated: (created) => {
        setRefreshKey((k) => k + 1);
        open(created);
      },
    });

  const viewSwitch = homeId ? null : (
    <div role="group" aria-label="View" className="flex items-center gap-0.5 rounded-md border border-line p-0.5">
      {(
        [
          ['list', 'List view', ListBulletIcon],
          ['calendar', 'Calendar view', CalendarDaysIcon],
        ] as const
      ).map(([mode, label, Icon]) => (
        <button
          key={mode}
          type="button"
          onClick={() => setView(mode)}
          aria-pressed={view === mode}
          aria-label={label}
          title={label}
          className={clsx(
            'rounded p-1 transition-colors',
            view === mode ? 'bg-fg/10 text-fg' : 'text-fg-muted hover:text-fg'
          )}
        >
          <Icon className="h-3.5 w-3.5" aria-hidden />
        </button>
      ))}
    </div>
  );

  const seriesScope = hoaId ?? (effectiveView === 'calendar' ? calendarHoaId : null);
  const trailing = (
    <>
      {seriesScope && (
        <>
          <Link
            to={`/hoa/${seriesScope}/reservations/utilization`}
            className="text-[12px] text-fg-muted hover:text-fg"
          >
            Utilization
          </Link>
          <Link
            to={`/hoa/${seriesScope}/reservations/revenue`}
            className="text-[12px] text-fg-muted hover:text-fg"
          >
            Revenue
          </Link>
          <Link
            to={`/hoa/${seriesScope}/reservations/suspensions`}
            className="text-[12px] text-fg-muted hover:text-fg"
          >
            Suspensions
          </Link>
        </>
      )}
      {hoaId && (
        <Link
          to={`/hoa/${hoaId}/amenities`}
          className="text-[12px] text-fg-muted hover:text-fg"
        >
          {canSetUp ? 'Set up amenities' : 'Amenities'}
        </Link>
      )}
      {canBook && (
        <button
          type="button"
          onClick={() =>
            openNewReservationSeries({
              ...(seriesScope ? { scope: { hoaId: seriesScope, homeId }, lockScope: !!hoaId } : {}),
              onCreated: (series) => {
                setRefreshKey((k) => k + 1);
                navigate(`/hoa/${series.hoa_id}/reservations/series/${series.id}`);
              },
            })
          }
          className="inline-flex items-center gap-1 text-[12px] text-fg-muted hover:text-fg"
          title="Book the same time every week for a household"
        >
          <ArrowPathIcon className="h-3.5 w-3.5" aria-hidden />
          Recurring
        </button>
      )}
      {canBook && <CreateButton label="New reservation" onClick={() => create()} />}
      {viewSwitch}
    </>
  );

  const pendingOnly = filters.statuses.length === 1 && filters.statuses[0] === 'pending';
  const calendarHoa = directory.hoas.find((h) => h.id === calendarHoaId);

  return (
    <SidebarLayout
      sidebar={<AppSidebar currentPage="reservations" />}
      navbar={<Navbar>{/* Your navbar content */}</Navbar>}
      fill
      flush
    >
      <div className="flex min-h-0 flex-1 flex-col">
        {effectiveView === 'calendar' ? (
          calendarHoaId ? (
            <ReservationCalendar
              hoaId={calendarHoaId}
              amenities={amenities}
              refreshKey={refreshKey}
              canBook={canBook}
              onSlotClick={(prefill) =>
                openNewReservation({
                  scope: { hoaId: calendarHoaId },
                  lockScope: true,
                  prefill,
                  onCreated: (created) => {
                    setRefreshKey((k) => k + 1);
                    open(created);
                  },
                })
              }
              onOpen={open}
              leading={
                spansHoas ? (
                  <PropertyPicker
                    options={directory.hoas.map((h) => ({ value: h.id, label: h.name }))}
                    value={calendarHoaId}
                    onChange={setCalendarHoaId}
                    placeholder="Association…"
                    ariaLabel={calendarHoa ? `Association: ${calendarHoa.name}` : 'Choose association'}
                    trigger={
                      <>
                        <HomeModernIcon className="h-3.5 w-3.5 text-fg-muted" aria-hidden />
                        {calendarHoa?.name ?? 'Association'}
                      </>
                    }
                  />
                ) : undefined
              }
              toolbar={trailing}
            />
          ) : (
            <LoadingSpinner />
          )
        ) : (
          <>
            <ErrorAlert className="m-3">{error}</ErrorAlert>
            <FilterBar
              fields={[
                {
                  field: 'Status',
                  options: STATUSES.map((s) => ({
                    value: s.value,
                    label: s.label,
                    icon: <ReservationStatusIcon status={s.value} />,
                  })),
                  values: filters.statuses,
                  onChange: (v) => setFilters((f) => ({ ...f, statuses: v as ReservationStatus[] })),
                  label: statusLabel,
                },
                {
                  field: 'Amenity',
                  options: amenityOptions,
                  values: filters.amenities,
                  onChange: (v) => setFilters((f) => ({ ...f, amenities: v })),
                },
                ...(spansHoas
                  ? [
                      {
                        field: 'HOA',
                        options: hoaOptions,
                        values: hoaFilter,
                        onChange: setHoaFilter,
                      },
                    ]
                  : []),
              ]}
              search={filters.search}
              onSearch={(search) => setFilters((f) => ({ ...f, search }))}
              searchLabel="Search reservations"
              onClearAll={() => {
                setFilters((f) => ({ ...f, statuses: [], amenities: [] }));
                setHoaFilter([]);
              }}
              shown={visible.length}
              total={rows.length}
              trailing={trailing}
            />
            <div className="min-h-0 flex-1 overflow-y-auto">
              {loading ? (
                <LoadingSpinner />
              ) : (
                <>
                  <GroupedList
                    groups={groups.map((g) => ({
                      key: g.status,
                      label: g.label,
                      icon: <ReservationStatusIcon status={g.status} />,
                      items: g.reservations,
                    }))}
                    renderRow={(r) => (
                      <ReservationRow key={r.id} reservation={r} onOpen={open} hideHome={!!homeId} />
                    )}
                    idPrefix="reservation-group"
                    emptyMessage={
                      pendingOnly
                        ? 'No requests waiting for approval.'
                        : rows.length === 0
                          ? 'No reservations yet.'
                          : 'No reservations match these filters.'
                    }
                  />
                  {total > rows.length && (
                    <p className="px-4 py-3 text-center text-[12px] text-fg-muted">
                      Showing the first {rows.length} of {total}. Narrow the filters to see the rest.
                    </p>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </SidebarLayout>
  );
};

export default ReservationsPage;
